import axios from 'axios';
import validator from 'validator';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import LoginLayout from './LoginLayout';
import { RootState } from 'redux/store';
import { useNavigate, useParams } from 'react-router-dom';
import useToggle from 'hooks/useToggle';
import Spinner from 'components/Spinner';
import { useState, useRef, useEffect } from 'react';
import { Button, Row, Col, Form } from 'react-bootstrap';
import PasswordChecklist from 'react-password-checklist';
import { useAppSelector, useAppDispatch } from 'redux/hooks';
import { SnackBarMessage, SnackBarMessageProps } from 'components/SnackBarMessage';

const BottomLink = () => {
    return (
        <Row className="mt-3">
            <Col className="text-center">
                <p className="text-muted">Back to<Link to={'/login'} className="text-muted ms-1">
                    <b>Log In</b>
                </Link>
                </p>
            </Col>
        </Row>
    );
};

const ResetPassword = () => {
    const form = useRef(null);
    const navigate = useNavigate();
    const { resetToken } = useParams();
    const dispatch = useAppDispatch();
    const [showPassword, togglePassword] = useToggle();
    const [loading, setLoading] = useState<boolean>(false);
    const [passwordValid, setPasswordValid] = useState(false);
    const [isValidated, setValidated] = useState<boolean>(false);
    const email = useAppSelector((state: RootState) => state.login.email);
    const [confirmPasswordValid, setConfirmPasswordValid] = useState(false);
    const password = useAppSelector((state: RootState) => state.login.password);
    const [isDisableFormSubmit, setIsDisableFormSubmit] = useState<boolean>(true);
    const confirmPassword = useAppSelector((state: RootState) => state.login.confirmPassword);
    const [snackSettings, setSnackSettings] = useState<SnackBarMessageProps>(new SnackBarMessageProps());

    useEffect(() => {
        if (!resetToken) {
            navigate('/login');
        }
    }, [resetToken]);


    useEffect(() => {
        handlePasswordChange(password, confirmPassword);
    }, [password, confirmPassword]);

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        event.stopPropagation();
        setLoading(true);

        // @ts-ignore
        if (form.current.checkValidity() === true) {
            sendEmail();
        }
        setValidated(true);
    }

    function sendEmail() {
        axios.post(`/api/v1/UserAuth/ResetPassword`,
            { resetToken: resetToken, newPassword: password },
            { headers: { 'Content-Encoding': 'gzip' } }
        ).then((result) => {
            if (result.data.data === true) {
                console.log(result.data);
                navigate('/login');
            }
            if (result.data.data === false) {
                setSnackSettings({
                    open: true,
                    messages: [result.data.error.toString()],
                    severity: 'error',
                    onClose: () => setSnackSettings({ ...snackSettings, open: false })
                })
            }
        }).catch((error) => {
            console.log(error);
        });
    }


    function handleChange(option: any, optionValue: any) {
        dispatch({
            type: 'loginSlice/updateField',
            payload: { key: option, value: optionValue }
        });
    }

    function handlePasswordChange(password: string, confirmPassword: string) {
        const isValidPassword = validator.isStrongPassword(password, {
            minLength: 8, minLowercase: 1,
            minUppercase: 1, minNumbers: 1, minSymbols: 1
        });
        setPasswordValid(isValidPassword);

        const isValidConfirmPassword = validator.isStrongPassword(confirmPassword, {
            minLength: 8, minLowercase: 1,
            minUppercase: 1, minNumbers: 1, minSymbols: 1
        });

        if (password === confirmPassword) {
            if (isValidPassword) {
                setConfirmPasswordValid(isValidConfirmPassword);
                setIsDisableFormSubmit(false);
            }
        } else {
            setConfirmPasswordValid(false);
            setIsDisableFormSubmit(true);
        }
    }

    return (
        <LoginLayout bottomLinks={<BottomLink />}>
            <div className="text-center m-auto">
                <h4 className="text-dark-50 text-center mt-0 font-weight-bold">Reset Password</h4>
                <p className="text-muted mb-4">Enter your new password</p>
            </div>
            <Form ref={form}
                noValidate autoComplete="off"
                onSubmit={(e) => handleSubmit(e)}>
                <div>
                    <Form.Group>
                        <Form.Label htmlFor="password">Password</Form.Label>
                        <div className="mb-0 input-group">
                            <Form.Control
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                id="password"
                                placeholder="Enter password"
                                required
                                value={password}
                                onChange={(e) => {
                                    handleChange("password", e.target.value);
                                }}
                                style={{ minHeight: '38px' }}
                            />
                            <div className={classNames('input-group-text', 'input-group-password', { 'show-password': showPassword, })} data-password={showPassword ? 'true' : 'false'}>
                                <span className="password-eye" onClick={togglePassword}></span>
                            </div>
                        </div>
                    </Form.Group>

                    <Form.Group className="mt-3 mb-3">
                        <Form.Label htmlFor="password">Confirm Password</Form.Label>
                        <div className="input-group">
                            <Form.Control
                                type={showPassword ? 'text' : 'password'}
                                name="confirm-password"
                                id="confirm-password"
                                placeholder="Enter password confirmation"
                                required
                                value={confirmPassword}
                                onChange={(e) => { handleChange("confirmPassword", e.target.value) }}
                                style={{ minHeight: '38px' }}
                            />
                            <div className={classNames('input-group-text', 'input-group-password', { 'show-password': showPassword, })} data-password={showPassword ? 'true' : 'false'}>
                                <span className="password-eye" onClick={togglePassword}></span>
                            </div>
                        </div>
                    </Form.Group>
                    <PasswordChecklist
                        rules={["minLength", "specialChar", "number", "capital", "match"]}
                        minLength={8}
                        value={password}
                        className="password-checklist"
                        valueAgain={confirmPassword}
                    />
                    <div className="mb-3 mb-0 text-center">
                        {isDisableFormSubmit === false &&
                            <Button variant="primary" className={classNames('btn', { 'disabled': isDisableFormSubmit, })} type="submit" disabled={loading}>
                                {loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
                                {loading ? <>Reseting Password...</> : <>Reset Password</>}
                            </Button>
                        }
                    </div>
                </div>
            </Form>
            <SnackBarMessage {...snackSettings} />
        </LoginLayout>
    );
};

export default ResetPassword;
